import { Link, useParams, useRouteContext } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Compass, Search, Sparkles, User, X } from "lucide-react";
import { Logo } from "./logo";
import { LocaleDropdown } from "../ui/locale-dropdown";
import { LocaleCurrencySwitcher } from "../ui/locale-currency-switcher";
import { AvatarDropdown } from "../ui/avatar-dropdown";
import { NotificationsDropdown } from "../ui/notifications-dropdown";
import { HeaderSearch } from "./header-search";
import { useSession } from "../../lib/auth";
import { useApplyAccountPreferences } from "../../features/account/hooks/use-preference-sync";
import { BucketIconButton } from "../../features/bucket/components/bucket-icon-button";
import { EmailVerificationBanner } from "./email-verification-banner";
import { SignInModal } from "../../features/auth/components/sign-in-modal";
import { CARD_FILL } from "../ui/card-frame";
import { EDGE_STYLES } from "../ui/pewter-edge";
import { useMenuGroup } from "../ui/use-presence";
import { AppHeaderButton } from "../app-promo/app-header-button";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const SOFT_TINT = "rgba(17,14,57,0.055)";
const PRIMARY_SHADOW = `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`;
/** Arrives once the page has moved under the bar; only its opacity animates. */
/**
 * Lighter than it was (owner, 2026-09-07). The old 0.35 at -14px spread read as
 * a grey band under the header rather than as the strip lifting off the page,
 * and it fought the pewter bands on the cards right below it.
 */
const HEADER_SHADOW = "0 10px 24px -16px rgba(17,14,57,0.22)";
/**
 * The pewter rail (owner, 2026-09-14). One pixel of the house band closes the
 * bottom of the strip, so the header ends on the same material the cards below
 * it are framed in instead of on nothing. This is the same gradient
 * `pewterFrame` paints — imported, never re-typed, or the two drift.
 *
 * It does supersede "no border on header" (2026-09-05): that ruling was about
 * the 4px band and the rounded inset the labs drew, which made the bar an
 * island. A single pixel across the full width is an edge, not an island.
 */
const PEWTER_RAIL = EDGE_STYLES.pewter;
/** At rest, and once the page has moved under the bar. */
const BAR_HEIGHT = 68;
const BAR_HEIGHT_COLLAPSED = 56;
const SINK_BY = BAR_HEIGHT - BAR_HEIGHT_COLLAPSED;
/**
 * The bar sinks 68 → 56 by transform only (the header-collapse rule: layout
 * animations stutter on iOS). The row keeps its 68px box; the surface slides
 * up by 12px and the content up by 6px, so it stays centred in what shows.
 * It used to animate `height`, which re-laid the page out on every frame.
 */
const SINK_MS = 200;
const SINK = `transition-transform duration-200 ease-[var(--mj-ease-out)] motion-reduce:transition-none`;

const NAV_LINK =
  "mj-press hidden min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold text-[#5a5d77] hover:bg-black/5 hover:text-[#110E39] lg:inline-flex";
const COLLAPSE = "transition-[max-width,opacity,transform] duration-200 ease-[var(--mj-ease-out)] motion-reduce:transition-none";

/**
 * One bar, no border, full width: the header is a plain strip on the sheet
 * tone with the search sunk into it and the primary in navy. The pewter band
 * and the rounded inset the labs used were both dropped by the owner ("no
 * border on header", "the full header", 2026-09-05); the bar separates from
 * the page by its shadow arriving on scroll instead. Fixed,
 * 68px tall, so $lang/_layout.tsx keeps its pt-[68px]; the verification
 * banner adds its own height above the bar.
 */
export function Header({
  phoneVariant = "default",
}: {
  /**
   * "minimal" (owner, 2026-09-26, option B, now the site default): below 768px the bar
   * is a globe, the wordmark centred and a round search button. Search opens
   * the full field in place of the row.
   */
  phoneVariant?: "default" | "minimal";
} = {}) {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const { data: session, isPending } = useSession();
  // One mount, on every page: the account's currency wins over the browser's.
  useApplyAccountPreferences();
  const ctx = useRouteContext({ strict: false }) as { hasSessionHint?: boolean };
  const hasSessionHint = !!ctx?.hasSessionHint;
  const locale = lang ?? "en";
  // Three-state auth slot:
  //  - sessionLoaded → render real shape from `session.user`.
  //  - !loaded but cookie hint present → avatar skeleton (matches eventual
  //    avatar shape, no Sign-In flash for returning users).
  //  - !loaded and no hint → Sign-In immediately (logged-out users skip the
  //    skeleton shimmer).
  const sessionLoaded = !isPending;
  const isAuthed = !!session?.user;
  const showAvatarSkeleton = !sessionLoaded && hasSessionHint;

  // Every menu on the bar shares one open slot, so two can never hang at once.
  const menus = useMenuGroup();
  const { setOpenName } = menus;

  // Past 60px the phone gives the row to the search; past 8px the shadow arrives.
  const [collapsed, setCollapsed] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  /**
   * While the suggestions are open on a narrow screen the field takes the
   * whole bar. A 390px phone left it about 200px wide, and the dropdown is
   * anchored to the field, so every suggestion came out as "Quad Bik…".
   */
  const [searchActive, setSearchActive] = useState(false);
  /** The minimal bar's search mode: the field has the whole row. */
  const [phoneSearch, setPhoneSearch] = useState(false);
  /** The field folding back into the button before the bar returns. */
  const [phoneSearchClosing, setPhoneSearchClosing] = useState(false);
  const closePhoneSearch = () => {
    setPhoneSearchClosing(true);
    window.setTimeout(() => {
      setPhoneSearch(false);
      setPhoneSearchClosing(false);
    }, 200);
  };
  const minimal = phoneVariant === "minimal";
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setCollapsed(y > 60);
      setScrolled(y > 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // A menu hanging from a control that has just collapsed has nothing to hang from.
  useEffect(() => {
    setOpenName(null);
  }, [collapsed, setOpenName]);

  /**
   * Publishes the header's visible height as `--mj-header-h`, for anything that
   * has to pin directly beneath it.
   *
   * The bar is not one height: it is 68px at rest, animates to 56px past 60px
   * of scroll, and the verification banner adds its own strip above it. The
   * category rail on the explore page pinned at a hard-coded `top-[68px]`,
   * so the moment the bar collapsed a ~12px slot opened between them and the
   * results grid scrolled through it, which made the rail read as a floating
   * island.
   *
   * Measured rather than computed from the constants, so the banner, the
   * pewter pixel and anything added to the strip later are all included
   * without a second place to update. The box no longer changes size when
   * the bar sinks (that is a transform), so the sink is subtracted here, once
   * per change rather than on every frame. Sinking, the value drops at once
   * and the bar slides over the rail; rising, it waits for the bar, so the
   * rail is always covered and never shows a gap.
   */
  const headerRef = useRef<HTMLElement>(null);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const writeHeaderH = useRef<() => void>(() => {});
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const write = () => {
      /*
       * `getBoundingClientRect` is zoom-scaled but a CSS `top` is not, and the
       * body carries `zoom: 0.9` from 768px up. Publishing the raw rect would
       * pin the rail ~6px too high and tuck it under the bar instead of below
       * it, so divide the zoom back out — the same correction `--mj-ui-zoom`
       * documents for viewport units.
       */
      const zoom = Number(getComputedStyle(document.documentElement).getPropertyValue("--mj-ui-zoom")) || 1;
      const height = el.getBoundingClientRect().height / zoom - (collapsedRef.current ? SINK_BY : 0);
      document.documentElement.style.setProperty("--mj-header-h", `${Math.round(height)}px`);
    };
    writeHeaderH.current = write;
    write();
    const observer = new ResizeObserver(write);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (collapsed || reduce) {
      writeHeaderH.current();
      return;
    }
    const id = window.setTimeout(() => writeHeaderH.current(), SINK_MS);
    return () => window.clearTimeout(id);
  }, [collapsed]);

  /** The row's content rides up half the sink, to stay centred in the 56px. */
  const rowShift = { transform: collapsed ? `translateY(-${SINK_BY / 2}px)` : "translateY(0)" };

  return (
    <header
      ref={headerRef}
      className={`mj-site-header pointer-events-none fixed inset-x-0 top-0 z-[var(--z-header)] w-full ${scrolled ? "mj-header-scrolled" : ""}`}
    >
      {/* Above the bar's surface, which slides up under it when the bar sinks.
          The header box keeps its full height while the bar shows less of it,
          so only what is drawn takes taps; the strip below passes through. */}
      <div className="pointer-events-auto relative z-[1]">
        <EmailVerificationBanner />
      </div>
      {/* Full width, edge to edge (owner: "the full header"): the surface is
          the whole strip, only the content is centred. The 1px of pewter is
          padding on the wrapper, so the band is the strip's own bottom edge
          and nothing has to be positioned over it. */}
      <div className="relative" style={{ paddingBottom: 1 }}>
        {/* The bar's surface: band, fill and shadow move as one layer, so its
            bottom edge rises 12px when the bar sinks without anything below
            it being laid out again. */}
        <div
          aria-hidden
          className={`pointer-events-auto absolute inset-0 ${SINK}`}
          style={{ transform: collapsed ? `translateY(-${SINK_BY}px)` : "translateY(0)" }}
        >
        {/* The shadow is a layer on the STRIP, not on the 1440px content box:
            on a wide screen a content-wide layer drew its left and right
            edges as a grey halo around the content, an island again. A
            strip-wide layer's sides fall outside the viewport, so only the
            bottom edge shows; only its opacity moves. It sits on the band's
            wrapper so it falls below the pewter pixel, not behind it. */}
        <span
          aria-hidden
          className="mj-header-shadow pointer-events-none absolute inset-x-0 -bottom-px top-0"
          style={{ boxShadow: HEADER_SHADOW }}
        />
        {/* The fill stays on a full-width layer: on a wide screen a 1440px fill
            would let the band's gradient show down both sides of the content. */}
          <div className="absolute inset-0" style={{ background: PEWTER_RAIL, paddingBottom: 1 }}>
            <div className="h-full" style={{ background: CARD_FILL }} />
          </div>
        </div>
        <div className="relative">
        {minimal ? (
          <nav
            className={`relative flex w-full items-center gap-2 px-3 md:hidden [&>*]:pointer-events-auto ${SINK}`}
            style={{ height: BAR_HEIGHT, ...rowShift }}
          >
            {/* The bar stays underneath and fades while the field grows over
                it out of the search button, so nothing blinks out. */}
              <div
                className={`flex w-full items-center transition-opacity duration-200 motion-reduce:transition-none ${
                  phoneSearch && !phoneSearchClosing ? "pointer-events-none opacity-0" : "opacity-100"
                }`}
                aria-hidden={phoneSearch || undefined}
              >
                <div className="flex w-10 shrink-0 justify-start">
                  <LocaleDropdown group={menus} compact align="start" />
                </div>
                <Link to="/$lang" params={{ lang: locale }} className="flex flex-1 items-center justify-center no-underline">
                  <Logo />
                </Link>
                <div className="flex w-10 shrink-0 justify-end">
                  <button
                    type="button"
                    onClick={() => setPhoneSearch(true)}
                    aria-label={t("common.search", { defaultValue: "Search" })}
                    className="mj-press grid h-10 w-10 place-items-center rounded-full text-white"
                    style={{ background: BEVEL_NAVY_FACE, boxShadow: PRIMARY_SHADOW }}
                  >
                    <Search size={17} strokeWidth={2.6} aria-hidden />
                  </button>
                </div>
              </div>
            {phoneSearch ? (
              // The round button stretches into the field (clip-path from the
              // button's circle to the full row) and folds back on close.
              <div className={`${phoneSearchClosing ? "mj-search-fold" : "mj-search-grow"} absolute inset-y-0 start-3 end-[9px] flex items-center gap-2`}>
                {/* Close sits at the start, so the field's own search button
                    lands exactly where the round button was: the button stays
                    put and the field grows out of it. */}
                <button
                  type="button"
                  onClick={closePhoneSearch}
                  aria-label={t("common.close", { defaultValue: "Close" })}
                  className="mj-press grid h-10 w-10 shrink-0 place-items-center rounded-full"
                  style={{ background: SOFT_TINT, color: "#110E39" }}
                >
                  <X size={18} strokeWidth={2.4} aria-hidden />
                </button>
                <div className="min-w-0 flex-1">
                  <HeaderSearch
                    group={menus}
                    compact={collapsed}
                    autoFocus
                    focusRing={false}
                    onActiveChange={(active) => {
                      setSearchActive(active);
                    }}
                    onDismiss={closePhoneSearch}
                  />
                </div>
              </div>
            ) : null}
          </nav>
        ) : null}
        <nav
          className={`relative mx-auto flex w-full max-w-[1440px] items-center gap-3 px-3 md:gap-4 md:px-7 [&>*]:pointer-events-auto ${SINK} ${minimal ? "max-md:hidden" : ""}`}
          style={{ height: BAR_HEIGHT, ...rowShift }}
        >
          {/* Under 640px the wordmark is the mark alone: at that width a
              160px wordmark plus the field left the field too narrow to read
              its own placeholder, and the search is what the bar is for. */}
          <div
            className={`flex items-center ${COLLAPSE} lg:!min-w-[160px] lg:!max-w-[185px] lg:!opacity-100 ${
              collapsed || searchActive
                ? "min-w-0 max-w-0 overflow-hidden opacity-0"
                : "min-w-0 max-w-[44px] opacity-100 sm:min-w-[160px] sm:max-w-[185px]"
            }`}
            aria-hidden={collapsed || searchActive ? true : undefined}
          >
            <Link
              to="/$lang"
              params={{ lang: locale }}
              className="flex items-center no-underline"
              tabIndex={collapsed || searchActive ? -1 : undefined}
            >
              <Logo className="hidden sm:block" />
              <img src="/images/majourneys-icon.svg" alt="MaJourneys" className="h-8 sm:hidden" />
            </Link>
          </div>

          {/* Shown at every width. Below md it used to appear only once the
              page had scrolled, which left the resting phone and tablet bar
              as a wordmark, a locale chip and a lot of nothing. */}
          {/* Shown at every width. Below md it used to appear only once the
              page had scrolled, which left the resting phone and tablet bar
              as a wordmark, a locale chip and a lot of nothing. The full bar
              stays (owner, 2026-10-05): it briefly became a round button that
              grew into the field, and the owner asked for the bar back. */}
          <div className="block min-w-0 max-w-[680px] flex-1">
            <HeaderSearch group={menus} compact={collapsed} onActiveChange={setSearchActive} />
          </div>

          <Link to="/$lang/discover" params={{ lang: locale }} className={NAV_LINK}>
            <Compass size={16} strokeWidth={2.2} className="shrink-0" aria-hidden="true" />
            <span>{t("nav.discover", { defaultValue: "Discover" })}</span>
          </Link>

          <Link to="/$lang/ai-chat" params={{ lang: locale }} className={NAV_LINK}>
            <Sparkles size={16} strokeWidth={2.2} className="shrink-0" aria-hidden="true" />
            <span>{t("nav.ai_chat")}</span>
            {/* Owner, 2026-09-24: flag the chat as a beta. Gold gradient, not a
                button, so it reads as a label on the link. */}
            <span
              className="ms-0.5 rounded-full px-1.5 py-px text-[10px] font-semibold uppercase leading-[14px] tracking-[0.04em]"
              style={{ background: "linear-gradient(135deg, #F7E3BC 0%, #EBC27E 55%, #E5B265 100%)", color: "#5E4318" }}
            >
              {t("nav.beta")}
            </span>
          </Link>

          <div className="ms-auto hidden min-w-[220px] items-center justify-end gap-2 lg:flex">
            <AppHeaderButton group={menus} />
            <LocaleCurrencySwitcher group={menus} />
            {/* Signed in, the bucket lives in the account menu with its count
                on the avatar (owner, 2026-10-11). A guest has no such menu, so
                the bag stays for them, and only once there is something in it. */}
            {sessionLoaded && !isAuthed ? <BucketIconButton hideWhenEmpty /> : null}
            {showAvatarSkeleton ? (
              <span
                className="h-10 w-10 shrink-0 animate-pulse rounded-full"
                style={{ background: SOFT_TINT }}
                aria-hidden="true"
              />
            ) : isAuthed ? (
              <>
                <NotificationsDropdown group={menus} />
                {/* The booking ticket's stub flies here once kept. */}
                <span className="inline-flex shrink-0" data-account-anchor="">
                  <AvatarDropdown group={menus} />
                </span>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setSignInOpen(true)}
                className="mj-press inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full ps-3 pe-5 text-[13.5px] font-semibold text-white"
                style={{ background: BEVEL_NAVY_FACE, boxShadow: PRIMARY_SHADOW }}
                aria-label={t("common.sign_in", { defaultValue: "Sign in" })}
              >
                <span
                  className="grid h-[22px] w-[22px] place-items-center rounded-full"
                  style={{ background: "rgba(255,255,255,0.12)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)" }}
                >
                  <User size={12} strokeWidth={2.4} aria-hidden />
                </span>
                <span className="leading-none">{t("common.sign_in", { defaultValue: "Sign in" })}</span>
              </button>
            )}
          </div>

          {/* Collapsed, the chip keeps its globe and drops only the two
              labels. It used to disappear entirely, which left a scrolled
              phone with no way to change language at all. */}
          <div
            className={`ms-auto flex items-center gap-2 ${COLLAPSE} lg:hidden ${
              searchActive ? "max-w-0 overflow-hidden opacity-0" : "max-w-[200px] opacity-100"
            }`}
            aria-hidden={searchActive ? true : undefined}
          >
            <LocaleDropdown group={menus} compact={collapsed} />
          </div>
        </nav>
        </div>
      </div>
      <div className="pointer-events-auto">
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    </header>
  );
}
